// /src/pages/DashboardsPage.js
//
// Live tiles from every bound source system, laid out on a grid.
//
// The grid is admin-arranged, one layout per dashboard, and saved server-side.
// Regular viewers get it read-only. An admin opens ?edit=1 (from the Dashboards
// admin screen) to drag and resize, then Save. So layout is configuration, not
// per-user state -- matching enable/disable and time-range.
//
// react-grid-layout is the one dependency the plain CSS grid could not cover:
// free-form reorder and resize with a serialisable {x,y,w,h} layout.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GridLayout, { useContainerWidth } from "react-grid-layout";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from "recharts";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const COLS = 12;
const ROW_H = 40;

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    const text = await res.text();
    let body = null;
    try { body = JSON.parse(text); } catch { /* keep raw */ }
    if (!res.ok) {
      const err = new Error(body?.detail || body?.error || `${res.status} ${res.statusText}`);
      err.payload = body;
      throw err;
    }
    return body;
  });
}

function isAdmin() {
  try {
    const role = JSON.parse(localStorage.getItem("user") || "{}").role || "";
    return ["admin", "ctc_admin"].includes(role);
  }
  catch { return false; }
}

/* ------------------------------------------------------------------ format */

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const moneyExact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
const counted = new Intl.NumberFormat("en-US");

function fmt(value, unit, { exact = false } = {}) {
  if (value === null || value === undefined) return "—";
  if (unit === "currency") return (exact ? moneyExact : money).format(value);
  if (unit === "percent") return `${value}%`;
  return counted.format(value);
}

function shortLabel(s) { return !s ? "" : s.length > 18 ? `${s.slice(0, 17)}…` : s; }

function relativeAge(iso) {
  if (!iso) return null;
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

const PERIOD_LABEL = { all: "", ytd: "YTD", mtd: "MTD", "12m": "Last 12 mo", "24m": "Last 24 mo" };

/* ------------------------------------------------------------------- charts */

function KpiBody({ rows, unit }) {
  const r = rows[0] || {};
  return (
    <div className="dash-kpi">
      <div className="dash-kpi-value">{fmt(r.value, unit, { exact: unit === "currency" })}</div>
      {r.value2 !== null && r.value2 !== undefined && (
        <div className="dash-kpi-sub">{counted.format(r.value2)} secondary</div>
      )}
    </div>
  );
}

function BarBody({ rows, unit }) {
  const data = rows.map((r) => ({ name: shortLabel(r.label), full: r.label, value: r.value }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={(v) => fmt(v, unit)} fontSize={11} />
        <YAxis type="category" dataKey="name" width={120} fontSize={11} />
        <Tooltip formatter={(v) => fmt(v, unit, { exact: true })}
                 labelFormatter={(_, p) => p?.[0]?.payload?.full ?? ""} />
        <Bar dataKey="value" fill="#2f6fb0" radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function LineBody({ rows, unit }) {
  const data = rows.map((r) => ({ name: r.label, value: r.value, value2: r.value2 }));
  const hasSecond = rows.some((r) => r.value2 !== null && r.value2 !== undefined);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={11} />
        <YAxis tickFormatter={(v) => fmt(v, unit)} fontSize={11} />
        <Tooltip formatter={(v) => fmt(v, unit, { exact: true })} />
        <Line type="monotone" dataKey="value" stroke="#2f6fb0" strokeWidth={2} dot={false} />
        {hasSecond && (
          <Line type="monotone" dataKey="value2" stroke="#9aa7b4" strokeWidth={2}
                strokeDasharray="4 3" dot={false} />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

function TableBody({ rows, unit }) {
  return (
    <div className="dash-table-wrap">
      <table className="table dash-table">
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.label}-${i}`}>
              <td>{r.label}</td>
              <td className="dash-num">{fmt(r.value, unit, { exact: true })}</td>
              {r.value2 !== null && r.value2 !== undefined && (
                <td className="dash-num dash-muted">{fmt(r.value2, unit)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BODIES = { kpi: KpiBody, bar: BarBody, line: LineBody, table: TableBody };

// Default footprint for a card that has no saved layout.
const DEFAULT_WH = {
  kpi:   { w: 3, h: 3 },
  table: { w: 4, h: 6 },
  bar:   { w: 6, h: 7 },
  line:  { w: 6, h: 7 },
};

/* --------------------------------------------------------------------- tile */

function Tile({ card, dashboardKey, editing, reloadKey }) {
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    // In edit mode the numbers are irrelevant and every fetch is a WAN round
    // trip, so skip loading data while arranging -- show a placeholder.
    if (editing) { setState({ status: "edit" }); return; }
    let alive = true;
    setState({ status: "loading" });
    api(`/dashboards/tiles/${card.source_key}/${card.metric_key}?dashboard=${encodeURIComponent(dashboardKey)}`)
      .then((d) => { if (alive) setState({ status: "ok", data: d }); })
      .catch((e) => { if (alive) setState({ status: "error", message: e.message }); });
    return () => { alive = false; };
  }, [card.source_key, card.metric_key, dashboardKey, editing, reloadKey]);

  const Body = BODIES[card.viz_type] || TableBody;
  const rows = state.data?.rows || [];
  const empty = state.status === "ok" && rows.length === 0;

  return (
    <div className={`card dash-tile${card.viz_type === "kpi" ? " dash-tile-kpi" : ""}`}>
      <div className="card-header dash-tile-head">
        <span>
          {card.title}
          {PERIOD_LABEL[card.period] && <span className="dash-period">{PERIOD_LABEL[card.period]}</span>}
        </span>
        {editing
          ? <span className="dash-src-tag">{card.product}</span>
          : state.status === "ok" && (
              <span className="dash-asof" title={state.data.as_of || ""}>{relativeAge(state.data.as_of)}</span>
            )}
      </div>
      <div className="dash-tile-body">
        {state.status === "loading" && <div className="dash-msg">Loading…</div>}
        {state.status === "edit" && <div className="dash-msg dash-muted">{card.viz_type} · drag / resize</div>}
        {state.status === "error" && <div className="dash-msg dash-err" title={state.message}>Source unavailable</div>}
        {empty && <div className="dash-msg dash-muted">No data</div>}
        {state.status === "ok" && !empty && <Body rows={rows} unit={card.unit} />}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- page */

export default function DashboardsPage({ title = "Dashboards", dashboardKey = "dashboards" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const admin = isAdmin();
  const wantEdit = new URLSearchParams(location.search).get("edit") === "1" && admin;
  // 2.x has no WidthProvider: this hook measures the container via ResizeObserver.
  const { width, containerRef, mounted } = useContainerWidth();

  const [cards, setCards] = useState([]);      // flat list across sources
  const [layout, setLayout] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const dq = `?dashboard=${encodeURIComponent(dashboardKey)}`;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api(`/dashboards/catalog${dq}`)
      .then((d) => {
        if (!alive) return;
        const flat = (d.sources || []).flatMap((s) =>
          s.tiles.map((t) => ({ ...t, source_key: s.source_key, product: s.product })));
        setCards(flat);
        setLayout(buildLayout(flat));
        setLoading(false);
      })
      .catch((e) => { if (alive) { setErr(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [dq]);

  // Pack cards that have no saved position; honour those that do.
  function buildLayout(flat) {
    let x = 0, y = 0, rowH = 0;
    return flat.map((c) => {
      const key = `${c.source_key}:${c.metric_key}`;
      if (c.layout && c.layout.x !== null && c.layout.x !== undefined) {
        return { i: key, ...c.layout };
      }
      const { w, h } = DEFAULT_WH[c.viz_type] || DEFAULT_WH.table;
      if (x + w > COLS) { x = 0; y += rowH; rowH = 0; }
      const item = { i: key, x, y, w, h };
      x += w; rowH = Math.max(rowH, h);
      return item;
    });
  }

  const saveLayout = () => {
    setSaving(true); setMsg("");
    const byKey = Object.fromEntries(layout.map((l) => [l.i, l]));
    const payload = cards.map((c) => {
      const l = byKey[`${c.source_key}:${c.metric_key}`] || {};
      return {
        source_key: c.source_key, metric_key: c.metric_key,
        enabled: true, params: { period: c.period || "all" },
        x: l.x, y: l.y, w: l.w, h: l.h,
      };
    });
    api(`/dashboards/admin/config/${dashboardKey}`, { method: "PUT", body: { cards: payload } })
      .then((r) => { setMsg(`Layout saved (${r.saved} cards).`); })
      .catch((e) => setMsg(`Save failed: ${e.message}`))
      .finally(() => setSaving(false));
  };

  const exitEdit = () => navigate(location.pathname, { replace: true });

  return (
    <div className="dash-page">
      <div className="dash-page-head">
        <h2>{title}{wantEdit && <span className="dash-editing"> — editing layout</span>}</h2>
        <div className="dash-head-actions">
          {wantEdit ? (
            <>
              {msg && <span className="dash-admin-msg">{msg}</span>}
              <button className="btn" onClick={saveLayout} disabled={saving}>
                {saving ? "Saving…" : "Save Layout"}
              </button>
              <button className="btn" onClick={exitEdit}>Done</button>
            </>
          ) : (
            <button className="btn" onClick={() => setReloadKey((k) => k + 1)}>Refresh</button>
          )}
        </div>
      </div>

      {loading && <div className="dash-msg">Loading…</div>}
      {err && <div className="alert-danger">{err}</div>}
      {!loading && !err && cards.length === 0 && (
        <div className="alert-secondary">
          {dashboardKey === "dashboards"
            ? "No source systems are bound. Tiles appear automatically once a source is connected."
            : "No tiles available for this dashboard. Either no source provides this data, or your department has not been granted it."}
        </div>
      )}

      {!loading && cards.length > 0 && (
        <div ref={containerRef}>
          {mounted && width > 0 && (
            <GridLayout
              className={`dash-rgl${wantEdit ? " is-editing" : ""}`}
              width={width}
              layout={layout}
              cols={COLS}
              rowHeight={ROW_H}
              margin={[12, 12]}
              isDraggable={wantEdit}
              isResizable={wantEdit}
              compactType="vertical"
              onLayoutChange={(l) => wantEdit && setLayout(l)}
              draggableCancel=".dash-tile-body"
            >
              {cards.map((c) => (
                <div key={`${c.source_key}:${c.metric_key}`}>
                  <Tile card={c} dashboardKey={dashboardKey} editing={wantEdit} reloadKey={reloadKey} />
                </div>
              ))}
            </GridLayout>
          )}
        </div>
      )}
    </div>
  );
}
